// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.3.0"
//
// Changelog:
//
// Version 1.3.0:
// - [Added] Route for clients to access the new AI Credits management page.
//
// Version 1.2.0:
// - [Added] Route for the new Client Groups feature page.
//
// Version 1.1.0:
// - [Added] Route for the new Resource Sharing feature page.
// ==============================================================================
import React, { JSX, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DateProvider } from './contexts/DateContext';
import { useAuth } from './hooks/useAuth';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import AuthLayout from './layouts/AuthLayout'; // Keep this import
import AppLayout from './layouts/AppLayout';
import MobileAppLayout from './layouts/MobileAppLayout';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import Spinner from './components/common/Spinner';
import { useIsMobile } from './hooks/useIsMobile';
import { UserRoleEnum, SubscriptionStatusEnum } from './types/enums';
import { LongPressProvider } from './features/long-press/LongPressContext';
import './i18n'; // Initialize i18n

// --- NEW IMPORTS ---
import WelcomePage from './pages/WelcomePage';
import PublicLayout from './layouts/PublicLayout';

// --- (all lazy imports remain the same) ---
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const WidgetLauncherPage = lazy(() => import('./pages/WidgetLauncherPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const RegisterByInvitationPage = lazy(() => import('./pages/RegisterByInvitationPage'));
const CoachRegisterPage = lazy(() => import('./pages/CoachRegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const RegistrationPendingPage = lazy(() => import('./pages/RegistrationPendingPage'));
const CoachAppointmentsPage = lazy(() => import('./pages/coach/CoachAppointmentsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const CoachDashboardPage = lazy(() => import('./pages/coach/CoachDashboardPage'));
const CoachAvailabilityPage = lazy(() => import('./pages/coach/CoachAvailabilityPage'));
const AppointmentsClientPage = lazy(() => import('./pages/client/AppointmentsClientPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const UserListPage = lazy(() => import('./features/admin/users/UserListPage'));
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const HabitsPage = lazy(() => import('./pages/HabitsPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const TodosPage = lazy(() => import('./pages/TodosPage'));
const WellBeingPage = lazy(() => import('./pages/WellBeingPage'));
const DailyQuotePage = lazy(() => import('./pages/DailyQuotePage'));
const WellBeingOrbPage = lazy(() => import('./pages/WellBeingOrbPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const TimeLogPage = lazy(() => import('./pages/TimeLogPage'));
const WheelOfLifePage = lazy(() => import('./pages/WheelOfLifePage'));
const WheelOfLifeAssessmentPage = lazy(() => import('./pages/WheelOfLifeAssessmentPage'));
const CoachClientsPage = lazy(() => import('./pages/coach/CoachClientsPage'));
const CoachInvitationsPage = lazy(() => import('./pages/coach/CoachInvitationsPage'));
const CoachSubscriptionPage = lazy(() => import('./pages/coach/CoachSubscriptionPage'));
const WeeklySummaryPage = lazy(() => import('./pages/WeeklySummaryPage'));
const ValuesJourneyPage = lazy(() => import('./pages/ValuesJourneyPage'));
const CoachValuesAssessmentPage = lazy(() => import('./pages/coach/CoachValuesAssessmentPage'));
const AICreditsPage = lazy(() => import('./pages/coach/AICreditsPage'));
const PublicCoachesPage = lazy(() => import('./pages/public/PublicCoachesPage'));
const PublicCoachProfilePage = lazy(() => import('./pages/public/PublicCoachProfilePage'));
const AICreditPacksPage = lazy(() => import('./pages/admin/AICreditPacksPage'));
const PaymentSuccessPage = lazy(() => import('./pages/payment/PaymentSuccessPage'));
const PaymentCancelledPage = lazy(() => import('./pages/payment/PaymentCancelledPage'));
const ClientDetailPage = lazy(() => import('./pages/coach/ClientDetailPage'));
const ClientDetailLayout = lazy(() => import('./layouts/ClientDetailLayout'));
const ClientPlannerView = lazy(() => import('./pages/coach/client-views/ClientPlannerView'));
const CoachToolsView = lazy(() => import('./pages/coach/client-views/CoachToolsView'));
const ClientInsightsView = lazy(() => import('./pages/coach/client-views/ClientInsightsView'));
const AssessmentSubmittedPage = lazy(() => import('./pages/coach/AssessmentSubmittedPage'));
const CoachTemplatesPage = lazy(() => import('./pages/coach/CoachTemplatesPage'));
// --- NEW ONBOARDING IMPORTS ---
const IntakeTemplatesPage = lazy(() => import('./pages/coach/IntakeTemplatesPage'));
const ClientOnboardingPage = lazy(() => import('./pages/client/ClientOnboardingPage'));
const AdminIntakeTemplatesPage = lazy(() => import('./pages/admin/AdminIntakeTemplatesPage'));
// --- NEW INVOICING IMPORTS ---
const CoachInvoicesPage = lazy(() => import('./pages/coach/CoachInvoicesPage'));
const ClientInvoicesPage = lazy(() => import('./pages/client/ClientInvoicesPage'));
// --- NEW RESOURCE SHARING IMPORT ---
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
// --- NEW CLIENT GROUPS IMPORT ---
const CoachGroupsPage = lazy(() => import('./pages/coach/CoachGroupsPage'));

// PublicOnlyRoute remains the same
const PublicOnlyRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { isAuthenticated, user, subscription, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>;
  }

  if (isAuthenticated && user) {
    if (user.role_name === UserRoleEnum.Admin) {
      return <Navigate to="/app/admin" replace />;
    }
    if (user.role_name === UserRoleEnum.Coach && subscription?.status === SubscriptionStatusEnum.Trial) {
      return <Navigate to="/app/coach/subscription" replace />;
    }
    return <Navigate to="/app" replace />;
  }
  return children;
};

// ProtectedRoute remains the same
const ProtectedRoute: React.FC<{ children: JSX.Element; allowedRoles?: string[] }> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-light">
        <Spinner size="lg" />
        <p className="ml-3 text-neutral-dark">Loading application...</p>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role_name)) {
    const roleDefaultPath = user.role_name === UserRoleEnum.Admin ? '/app/admin' :
                            user.role_name === UserRoleEnum.Coach ? '/app/coach-dashboard' :
                            '/app';
    return <Navigate to={roleDefaultPath} replace />;
  }
  return children;
};


const ResponsiveAppLayout: React.FC = () => {
  const isMobile = useIsMobile();
  return isMobile ? <MobileAppLayout /> : <AppLayout />;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <Router basename="/lifeline">
          <AuthProvider>
            <DateProvider>
              <LongPressProvider>
              <Toaster position="top-right" reverseOrder={false} toastOptions={{
                duration: 4000,
                className: 'rounded-md bg-neutral-darkest text-white shadow-lg',
                success: { className: 'rounded-md bg-green-600 text-white shadow-lg' },
                error: { className: 'rounded-md bg-red-600 text-white shadow-lg' }
              }} />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-neutral-light"><Spinner size="xl" /><p className="ml-4 text-xl">Loading Page...</p></div>}>
                <Routes>
                  {/* Public routes that use the PublicLayout (e.g., welcome page) */}
                  <Route element={<PublicLayout />}>
                    <Route path="welcome" element={<WelcomePage />} />
                    {/* Public facing coach list and profile pages */}
                    <Route path="coaches" element={<PublicCoachesPage />} />
                    <Route path="coaches/:coachId" element={<PublicCoachProfilePage />} />
                  </Route>

                  {/* Auth routes that use the AuthLayout (e.g., login, register, forgot password) */}
                  {/* MODIFIED: AuthLayout now wraps its specific routes */}
                  <Route element={<AuthLayout />}>
                    {/* LoginPage is direct child of AuthLayout. Its internal state should persist. */}
                    <Route path="login" element={<LoginPage />} /> 
                    {/* Other registration/password reset pages still use PublicOnlyRoute within AuthLayout */}
                    <Route path="register/coach" element={<PublicOnlyRoute><CoachRegisterPage /></PublicOnlyRoute>} />
                    <Route path="register/pending" element={<PublicOnlyRoute><RegistrationPendingPage /></PublicOnlyRoute>} />
                    <Route path="register-by-invitation/:token" element={<PublicOnlyRoute><RegisterByInvitationPage /></PublicOnlyRoute>} />
                    <Route path="verify-email" element={<PublicOnlyRoute><VerifyEmailPage /></PublicOnlyRoute>} />
                    <Route path="forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
                    <Route path="reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
                  </Route>

                  {/* Protected app routes */}
                  <Route element={<ProtectedRoute><ResponsiveAppLayout /></ProtectedRoute>}>
                    <Route path="app">
                      <Route index element={<AppIndexChooser />} />
                      {/* --- NEW ONBOARDING ROUTE FOR CLIENTS --- */}
                      <Route path="onboarding" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><ClientOnboardingPage /></ProtectedRoute>} />
                      <Route path="time-log" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><TimeLogPage /></ProtectedRoute>} />
                      <Route path="goals" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><GoalsPage /></ProtectedRoute>} />
                      <Route path="habits" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><HabitsPage /></ProtectedRoute>} />
                      <Route path="journal" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><JournalPage /></ProtectedRoute>} />
                      <Route path="todos" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><TodosPage /></ProtectedRoute>} />
                      <Route path="well-being" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><WellBeingPage /></ProtectedRoute>} />
                      <Route path="daily-quote" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><DailyQuotePage /></ProtectedRoute>} />
                      <Route path="well-being-orb" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><WellBeingOrbPage /></ProtectedRoute>} />
                      <Route path="wheel-of-life" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><WheelOfLifePage /></ProtectedRoute>} />
                      <Route path="weekly-summary" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><WeeklySummaryPage /></ProtectedRoute>} />
                      <Route path="values-journey" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><ValuesJourneyPage /></ProtectedRoute>} />
                      <Route path="wheel-of-life/assessment" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach, UserRoleEnum.Admin]}><WheelOfLifeAssessmentPage /></ProtectedRoute>} />
                      <Route path="messages" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client, UserRoleEnum.Coach]}><MessagesPage /></ProtectedRoute>} />
                      <Route path="notifications" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client, UserRoleEnum.Coach, UserRoleEnum.Admin]}><NotificationsPage /></ProtectedRoute>} />
                      <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                      <Route path="ai" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client, UserRoleEnum.Coach, UserRoleEnum.Admin]}><AIAssistantPage /></ProtectedRoute>} />
                      <Route path="coach-dashboard" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachDashboardPage /></ProtectedRoute>} />                      
                      
                      {/* --- NEW RESOURCE SHARING ROUTE --- */}
                      <Route path="resources" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client, UserRoleEnum.Coach]}><ResourcesPage /></ProtectedRoute>} />

                      <Route path="coach/client/:clientId" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><ClientDetailLayout /></ProtectedRoute>}>
                          <Route index element={<ClientDetailPage />} />
                          <Route path="planner" element={<ClientPlannerView />} />
                          <Route path="tools" element={<CoachToolsView />} />
                          <Route path="insights" element={<ClientInsightsView />} />
                      </Route>
                      <Route path="coach/client/:clientId/values-assessment" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachValuesAssessmentPage /></ProtectedRoute>} />
                      <Route path="coach/client/:clientId/values-assessment/submitted" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><AssessmentSubmittedPage /></ProtectedRoute>} />
                      
                      {/* --- NEW ONBOARDING ROUTE FOR COACHES --- */}
                      <Route path="coach/intake-templates" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><IntakeTemplatesPage /></ProtectedRoute>} />
                      <Route path="coach/availability" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachAvailabilityPage /></ProtectedRoute>} />
                      <Route path="coach/clients" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachClientsPage /></ProtectedRoute>} />
                      <Route path="coach/invitations" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachInvitationsPage /></ProtectedRoute>} />
                      <Route path="coach/templates" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachTemplatesPage /></ProtectedRoute>} />
                      <Route path="appointments" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachAppointmentsPage /></ProtectedRoute>} />
                      <Route path="coach/subscription" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachSubscriptionPage /></ProtectedRoute>} />
                      <Route path="coach/ai-credits" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><AICreditsPage /></ProtectedRoute>} />

                      {/* --- NEW CLIENT AI CREDITS ROUTE --- */}
                      <Route path="ai-credits" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><AICreditsPage /></ProtectedRoute>} />

                      <Route path="client/appointments-hub" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><AppointmentsClientPage /></ProtectedRoute>} />
                      
                      {/* --- NEW INVOICING ROUTES --- */}
                      <Route path="coach/invoices" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachInvoicesPage /></ProtectedRoute>} />
                      <Route path="client/invoices" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Client]}><ClientInvoicesPage /></ProtectedRoute>} />

                      {/* --- NEW CLIENT GROUPS ROUTE --- */}
                      <Route path="coach/groups" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Coach]}><CoachGroupsPage /></ProtectedRoute>} />

                      <Route path="admin" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Admin]}><AdminPage /></ProtectedRoute>} />
                      <Route path="admin/users" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Admin]}><UserListPage /></ProtectedRoute>} />
                      <Route path="admin/ai-credit-packs" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Admin]}><AICreditPacksPage /></ProtectedRoute>} />
                       {/* --- NEW ADMIN ROUTE FOR INTAKE FORMS --- */}
                      <Route path="admin/intake-templates" element={<ProtectedRoute allowedRoles={[UserRoleEnum.Admin]}><AdminIntakeTemplatesPage /></ProtectedRoute>} />
                    </Route>

                    <Route path="payment/success" element={<PaymentSuccessPage />} />
                    <Route path="payment/cancelled" element={<PaymentCancelledPage />} />
                  </Route>

                  {/* Root and fallback routes */}
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
              </LongPressProvider>
            </DateProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

// ... (AppIndexChooser component remains the same) ...
const AppIndexChooser: React.FC = () => {
  const { user, subscription, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role_name === UserRoleEnum.Admin) {
    return <Navigate to="/app/admin" replace />;
  }

  if (user.role_name === UserRoleEnum.Coach) {
    if (subscription?.status === SubscriptionStatusEnum.Trial) {
      return <Navigate to="/app/coach/subscription" replace />;
    }
    return <Navigate to="/app/coach-dashboard" replace />;
  }

  return <WidgetLauncherPage />;
};

const RootRedirect = () => {
  const { isAuthenticated, isLoading, user, subscription } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size="lg" /></div>;
  }

  if (isAuthenticated && user) {
    if (user.role_name === UserRoleEnum.Coach && subscription?.status === SubscriptionStatusEnum.Trial) {
        return <Navigate to="/app/coach/subscription" replace />;
    }
    return <Navigate to="/app" replace />;
  }
  return <Navigate to="/welcome" replace />;
};


export default App;